import React, { useState, useEffect } from 'react';
import ResponsiveImage from './ResponsiveImage';

/**
 * BlurUpImage Component
 * Implements progressive image loading with blur-up effect:
 * 1. Shows a tiny blurred version first (LQIP)
 * 2. Loads the full image in the background
 * 3. Smoothly transitions from blurred to full image when loaded
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Original image source
 * @param {string} props.size - Size identifier (thumbnail, small, medium, large)
 * @param {string} props.alt - Image alt text
 * @param {string} props.className - Custom class names
 * @param {string} props.containerClassName - Class for the container div
 * @param {boolean} props.disableBlurUp - Disable blur-up effect
 * @param {Object} props.imgProps - Additional props for the img element
 */
const BlurUpImage = ({
  src,
  size = 'medium',
  alt = '',
  className = '',
  containerClassName = '',
  disableBlurUp = false,
  imgProps = {},
  ...rest
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const basePath = process.env.PUBLIC_URL;

  // Extract the filename from the path
  const getFilename = (path) => {
    if (!path) return '';
    return path.replace(`${basePath}/assets/`, '').split('/').pop();
  };

  // Generate LQIP path
  const getLQIPPath = (path) => {
    if (!path) return '';
    const filename = getFilename(path);
    return `${basePath}/assets/optimized/lqip/${filename}`;
  };

  // Create Intersection Observer to check when image is in viewport
  useEffect(() => {
    if (disableBlurUp) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading when image is 200px from viewport
    );

    const currentElement = document.getElementById(`blur-container-${getFilename(src).replace('.', '-')}`);
    if (currentElement) observer.observe(currentElement);

    return () => {
      if (currentElement) observer.disconnect();
    };
  }, [src, disableBlurUp]);

  // Preload the full image when in viewport
  useEffect(() => {
    if (!isVisible || disableBlurUp) return;

    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoaded(true);
  }, [isVisible, src, disableBlurUp]);

  // If blur-up is disabled, just render the responsive image
  if (disableBlurUp) {
    return <ResponsiveImage src={src} size={size} alt={alt} className={className} {...rest} />;
  }

  const id = `blur-container-${getFilename(src).replace('.', '-')}`;

  return (
    <div
      id={id}
      className={`relative overflow-hidden ${containerClassName}`}
      style={{ backgroundColor: '#f3f4f6' }}
    >
      {/* Blur placeholder */}
      <div className={`absolute inset-0 ${isLoaded ? 'animate-fadeOut' : ''}`}>
        <img
          src={getLQIPPath(src)}
          alt=""
          className={`w-full h-full object-cover transition-opacity duration-500 filter blur-sm ${
            isLoaded ? 'opacity-0' : 'opacity-100'
          } ${className}`}
          aria-hidden="true"
        />
      </div>

      {/* Main image (only load when near viewport) */}
      <div
        className={`transition-opacity duration-500 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {isVisible && (
          <ResponsiveImage
            src={src}
            size={size}
            alt={alt}
            className={className}
            imgProps={{
              ...imgProps,
              onLoad: () => setIsLoaded(true),
            }}
            {...rest}
          />
        )}
      </div>
    </div>
  );
};

export default BlurUpImage; 